/**
 * PayrollEvolutionPrint — page « Evolution pluriannuelle » du PDF Planification.
 * Trajectoire de la masse salariale N-1 → N+5 (brut, charges, net, coût employeur,
 * EPT moyen, effectif). Réplique l'onglet (plans serveur via fetchMultiYearPayroll) — table
 * uniquement (le graphe écran n'est pas repris en impression).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../../i18n/useP';
import { listHrContracts, listPayrollMovements } from '../../../api/hr';
import { fetchMultiYearPayroll } from '../payrollEvolution';
import { formatNumber } from '../../../utils/format';
import PrintReportHeader from './PrintReportHeader';

const PAST = 1;
const FUTURE = 5;
const fmtChf = (n) => formatNumber(Math.round(Number(n) || 0), 0);

export default function PayrollEvolutionPrint({ activeTenant, year, editedAt, onDataReady }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const N = year || new Date().getFullYear();
  const years = useMemo(() => Array.from({ length: PAST + 1 + FUTURE }, (_, i) => N - PAST + i), [N]);
  /* Plans annuels CALCULÉS PAR LE SERVEUR (moteur migré du JavaScript le
     2026-08-21) — mêmes chiffres que l'onglet écran. */
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!tenantId) return;
    Promise.all([
      listHrContracts(tenantId),
      listPayrollMovements(tenantId),
    ]).then(([c, m]) => Promise.all([
      fetchMultiYearPayroll({
        tenantId, contracts: c.data || [], movements: m.data || [],
        years, estimatedFrom: N + 1, ratesYear: N,
      }),
    ])).then(([rs]) => {
      setRows(rs);
      onDataReady && onDataReady('payroll-evolution');
    }).catch(() => {});
  }, [tenantId, N, years]); // eslint-disable-line

  const lines = [
    { label: 'Salaires bruts', get: (r) => r.gross, kind: 'money', strong: true },
    { label: t('phrases:p_0834336deb'), get: (r) => -r.chargesEmpl, kind: 'money' },
    { label: '= Salaire net', get: (r) => r.net, kind: 'money', strong: true },
    { label: 'Charges employeur', get: (r) => r.chargesEmpr, kind: 'money' },
    { label: p('= Coût employeur'), get: (r) => r.cout, kind: 'money', strong: true },
    { label: 'EPT moyen', get: (r) => r.fteAvg, kind: 'fte' },
    { label: 'Effectif (fin)', get: (r) => r.hcEnd, kind: 'int' },
    { label: t('phrases:p_21c92dab87'), get: (r) => r.costPerFte, kind: 'money' },
  ];
  const fmtCell = (kind, v) => (kind === 'fte' ? formatNumber(v, 1) : kind === 'int' ? formatNumber(v, 0) : fmtChf(v));

  return (
    <section className="ppl-print__section">
      <PrintReportHeader category="Planification" title="Evolution pluriannuelle" year={N} tenant={activeTenant} editedAt={editedAt} />

      <table className="ppl-print__table ppl-print__table--total-once">
        <caption>Masse salariale {N - PAST} → {N + FUTURE} · années &gt; {N} aux taux de {N} (estimé) · CHF</caption>
        <thead>
          <tr>
            <th> </th>
            {rows.map((r) => (
              <th key={r.year} className="num nowrap">{r.year}{r.estimated ? ' (est.)' : ''}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map((ln) => (
            <tr key={ln.label} className={ln.strong ? 'ppl-print__row--strong' : ''}>
              <td>{ln.strong ? <strong>{ln.label}</strong> : ln.label}</td>
              {rows.map((r) => (
                <td key={r.year} className="num nowrap">{fmtCell(ln.kind, ln.get(r))}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
